/**
 * 予実表 (Budget) の純粋ヘルパー関数。
 *
 * 純リファクタで index.tsx から切り出したもの (挙動・実装は完全に元のまま)。
 * React / コンポーネント state に依存しない純関数のみを置く。
 */

import type {
  BudgetRecord,
  BudgetDetailRow as ApiBudgetDetailRow,
  BudgetMonthData,
} from "@/store/budgetSlice";
import type { BudgetDetailRow, BudgetRow } from "./budgetTypes";

const EXPENSE_GROUPS = new Set(["expense", "expense_opex", "expense_capex"]);

export function isExpenseBudgetGroup(group?: string): boolean {
  return !!group && EXPENSE_GROUPS.has(group);
}

export function subtractWhenBothEntered(
  income: number | null,
  expense: number | null,
): number | null {
  return income !== null && expense !== null ? income - expense : null;
}

export function getOrderedMonthKeys(startMonth: number): string[] {
  const keys: string[] = [];
  for (let i = 0; i < 12; i++) {
    const m = ((startMonth - 1 + i) % 12) + 1;
    keys.push(String(m).padStart(2, "0"));
  }
  return keys;
}

export function getMonthLabels(startMonth: number): string[] {
  return getOrderedMonthKeys(startMonth).map((k) => `${parseInt(k, 10)}月`);
}

export function canEditCell(
  _row: { group: string; category: string },
  _monthKey: string,
  _fiscalYear: string,
  _field: "budget" | "actual",
  viewMode: string,
): boolean {
  // カテゴリ行も予算・実績ともに直接編集可 (月別単位)。
  // 編集すると BudgetMonthData.manual_override.{budget|actual}=true が立ち、
  // 内訳の自動再集計で上書きされなくなる。
  // 内訳合計 > 手動値 の場合は BE 側でバリデーションエラー (400) を返す。
  if (viewMode !== "monthly") return false;
  return true;
}

/**
 * 詳細行 (個別工事 / 個別区画 等) のセル編集可否。
 * - 予算: 編集可
 * - 実績: 編集可 (個別レコードを直接 update or upsert する)
 */
export function canEditDetailCell(
  _row: { group: string },
  _monthKey: string,
  _fiscalYear: string,
  _field: "budget" | "actual",
  viewMode: string,
): boolean {
  if (viewMode !== "monthly") return false;
  return true;
}

export function getExpenseDetailEditableMonthIndex(
  detail: BudgetDetailRow,
): number | null {
  const idx = detail.months.findIndex(
    (cell) =>
      (cell?.budget ?? null) !== null ||
      (cell?.actual ?? null) !== null ||
      (cell?.manual_actual ?? null) !== null,
  );
  return idx >= 0 ? idx : null;
}

export function canEditBudgetDetailCell(params: {
  row: { group: string };
  detail: BudgetDetailRow;
  monthIndex: number;
  monthKey: string;
  fiscalYear: string;
  field: "budget" | "actual";
  viewMode: string;
}): boolean {
  if (
    !canEditDetailCell(
      params.row,
      params.monthKey,
      params.fiscalYear,
      params.field,
      params.viewMode,
    )
  ) {
    return false;
  }
  if (!isExpenseBudgetGroup(params.row.group)) return true;
  return (
    getExpenseDetailEditableMonthIndex(params.detail) === params.monthIndex
  );
}

export function apiRecordToBudgetRow(
  record: BudgetRecord,
  orderedKeys: string[],
): BudgetRow {
  // 実績列は全ステータス (予定/未払い/支払済) を含む actual を表示する。
  // 「項目があるのに 0 表示」を避けるため、paid (支払済のみ) ではなく actual を採用。
  // null (未入力) はそのまま保持し、表示側で "-" (空白) を出す。0 (明示ゼロ) と区別する (§7-I)。
  const months = orderedKeys.map((key) => ({
    budget: record.months?.[key]?.budget ?? null,
    actual: record.months?.[key]?.actual ?? null,
    manual_override: record.months?.[key]?.manual_override,
    override_resolved: (record.months?.[key] as any)?.override_resolved,
  }));

  const details: BudgetDetailRow[] | undefined = record.details?.map(
    (d: ApiBudgetDetailRow, idx: number) => ({
      id: d.section_id || `detail-${idx}`,
      label: d.label,
      sectionId: d.section_id,
      sectionCategory: d.section_category,
      revenueType: d.revenue_type,
      months: orderedKeys.map((key) => ({
        budget: d.months?.[key]?.budget ?? null,
        actual: d.months?.[key]?.actual ?? null,
        manual_actual: (d.months?.[key] as any)?.manual_actual ?? null,
        actual_source: (d.months?.[key] as any)?.actual_source,
      })),
    }),
  );

  return {
    id: record.sort_key,
    category: record.category,
    categoryId: record.category_id,
    group: record.category_type,
    months,
    details: details && details.length > 0 ? details : undefined,
  };
}

export function buildEmptyMonths(
  orderedKeys: string[],
): Record<string, BudgetMonthData> {
  const m: Record<string, BudgetMonthData> = {};
  for (const key of orderedKeys) {
    // 新規レコードは未入力 = null。0 (明示ゼロ) と区別する (§7-I)。
    // 0 埋めにすると科目追加時に勝手に ¥0 が入ってしまう。
    m[key] = { budget: null, actual: null };
  }
  return m;
}
